import React, { useEffect, useState } from "react";
import api from "./api";
import SalesRepPerformanceWidget from "./SalesRepPerformanceWidget";


const MEDIA_BASE_URL =
  import.meta.env.VITE_API_URL || "https://crm.backend.thefither.com/";
/* =========================
   ADMIN TABS
========================= */
const TABS = [
    "Dashboard",
    "table",
    "Leads",
    "Conversations",
    "Sales Team",
    "Voice & Media",
    "Analytics",
    "Settings",
];

/* =========================
   REUSABLE UI
========================= */
const Card = ({ title, children }) => (
    <div className="bg-white rounded-lg shadow-sm p-5">
        {title && (
            <h3 className="text-sm font-medium text-gray-700 mb-3">
                {title}
            </h3>
        )}
        {children}
    </div>
);

const Table = ({ columns, data }) => (
    <table className="w-full text-sm">
        <thead className="text-gray-500 border-b">
            <tr>
                {columns.map((c) => (
                    <th key={c} className="text-left py-2">
                        {c}
                    </th>
                ))}
            </tr>
        </thead>
        <tbody>
            {data.length === 0 ? (
                <tr>
                    <td colSpan={columns.length} className="py-4 text-center text-gray-400">
                        No data
                    </td>
                </tr>
            ) : (
                data.map((row, i) => (
                    <tr key={i} className="border-b last:border-0">
                        {Object.values(row).map((v, j) => (
                            <td key={j} className="py-2">
                                {v ?? "—"}
                            </td>
                        ))}
                    </tr>
                ))
            )}
        </tbody>
    </table>
);

/* =========================
   MAIN COMPONENT
========================= */
export default function AdminPanel() {
    const [activeTab, setActiveTab] = useState("Dashboard");
    const [loading, setLoading] = useState(false);

    const [dashboard, setDashboard] = useState({});
    const [leads, setLeads] = useState([]);
    const [media, setMedia] = useState([]);
    const [salesTeam, setSalesTeam] = useState([]);
    const [analytics, setAnalytics] = useState({});
    const [conversations, setConversations] = useState([]);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [showTodayOnly, setShowTodayOnly] = useState(false);
    
    const [selectedLead, setSelectedLead] = useState(null);
    const [leadMessages, setLeadMessages] = useState([]);
    const openLead = async (lead) => {
        setSelectedLead(lead);
        try {
          const res = await api.get(
            `/api/leads/messages/${lead.id}?limit=100`
          );
          setLeadMessages(res.data?.data || []);
        } catch (e) {
          console.error("Failed to load messages", e);
        }
      };
      
    /* =========================
       FETCH DATA ON TAB CHANGE
    ========================= */
    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);

                if (activeTab === "Dashboard") {
                    const res = await api.get("/api/admin/dashboard");
                    setDashboard(res.data);
                }

                if (activeTab === "Leads") {
                    const res = await api.get("/api/admin/leads");
                    setLeads(res.data || []);
                }

                if (activeTab === "Voice & Media") {
                    const res = await api.get("/api/admin/media");
                    setMedia(res.data || []);
                }

                if (activeTab === "Sales Team") {
                    const res = await api.get("/api/admin/sales-team");
                    setSalesTeam(res.data || []);
                }
                if (activeTab === "Conversations") {
                    const res = await api.get("/api/admin/conversations");
                    setConversations(res.data || []);
                }

                if (activeTab === "Analytics") {
                    const res = await api.get("/api/admin/analytics");
                    setAnalytics(res.data || {});
                }
            } catch (err) {
                console.error("Admin API error:", err);
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [activeTab]);

    /* =========================
       TAB CONTENT
    ========================= */
     const filteredLeads = leads.filter((l) => {
        const matchesSearch =
          !search ||
          l.phone?.includes(search) ||
          String(l.id).includes(search);
      
        const matchesStatus =
          statusFilter === "all" || l.status === statusFilter;
      
        const matchesToday =
          !showTodayOnly ||
          (l.created_at &&
            new Date(l.created_at).toDateString() ===
              new Date().toDateString());
      
        return matchesSearch && matchesStatus && matchesToday;
      });
    const renderContent = () => {
        if (loading) {
            return (
                <div className="text-sm text-gray-500">
                    Loading…
                </div>
            );
        }

        switch (activeTab) {
            case "Dashboard":
                return (
                    
                    <div className="grid grid-cols-4 gap-4">
                        <div className="dashboard-grid">
      <SalesRepPerformanceWidget />
      {/* other widgets */}
    </div>
                        <Card title="Total Leads">
                            <p className="text-2xl font-semibold">
                                {dashboard.totalLeads || 0}
                            </p>
                        </Card>
                        <Card title="New Today">
                            <p className="text-2xl font-semibold">
                                {dashboard.newToday || 0}
                            </p>
                        </Card>
                        <Card title="Active Chats">
                            <p className="text-2xl font-semibold">
                                {dashboard.activeChats || 0}
                            </p>
                        </Card>
                        <Card title="Voice Usage %">
                            <p className="text-2xl font-semibold">
                                {analytics.voiceRatio ?? "—"}%
                            </p>
                        </Card>
                    </div>
                );

                case "Leads":
                    return (
                      <div className="grid grid-cols-3 gap-6">
                        {/* LEFT: LEADS LIST */}
                        <div className="col-span-2">
                          <Card title="Leads">
                            {/* FILTER BAR */}
                            <div className="flex gap-3 mb-4">
                              <input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search by phone or ID"
                                className="border rounded px-3 py-2 text-sm w-56"
                              />
                  
                              <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="border rounded px-3 py-2 text-sm"
                              >
                                <option value="all">All</option>
                                <option value="new">New</option>
                                <option value="interested">Interested</option>
                                <option value="converted">Converted</option>
                                <option value="lost">Lost</option>
                              </select>
                  
                              <label className="flex items-center gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={showTodayOnly}
                                  onChange={(e) =>
                                    setShowTodayOnly(e.target.checked)
                                  }
                                />
                                Today only
                              </label>
                            </div>
                  
                            {/* TABLE */}
                            <Table
                              columns={[
                                "ID",
                                "Phone",
                                "Status",
                                "Assigned Rep",
                                "Last Message",
                              ]}
                              data={filteredLeads.map((l) => ({
                                id: (
                                  <button
                                    onClick={() => openLead(l)}
                                    className="text-teal-600 underline"
                                  >
                                    #{l.id}
                                  </button>
                                ),
                                phone: l.phone || "—",
                                status: l.status,
                                rep: l.assigned_to || "—",
                                last: l.last_message_at
                                  ? new Date(l.last_message_at).toLocaleString()
                                  : "—",
                              }))}
                            />
                          </Card>
                        </div>
                  
                        {/* RIGHT: CONVERSATION */}
                        <div>
                          <Card title="Conversation">
                            {!selectedLead ? (
                              <p className="text-sm text-gray-500">
                                Select a lead to view messages
                              </p>
                            ) : (
                              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                                {leadMessages.map((m) => (
                                  <div
                                    key={m.id}
                                    className={`p-2 rounded text-sm ${
                                      m.direction === "IN"
                                        ? "bg-gray-100"
                                        : "bg-teal-600 text-white"
                                    }`}
                                  >
                                    <p className="text-xs opacity-70 mb-1">
                                      {m.direction === "IN"
                                        ? "Customer"
                                        : "Rep"}{" "}
                                      ·{" "}
                                      {new Date(m.created_at).toLocaleTimeString()}
                                    </p>
                  
                                    {m.message_type === "text" && m.message}
                  
                                    {m.message_type === "audio" && (
                                      <audio
                                        controls
                                        src={`${MEDIA_BASE_URL}${m.media_url}`}
                                        className="w-full"
                                      />
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </Card>
                        </div>
                      </div>
                    );
                  
            case "Voice & Media":
                return (
                    <Card title="Voice & Media Logs">
                        <Table
                            columns={[
                                "Type",
                                "Lead",
                                "Rep",
                                "Duration",
                                "Size",
                                "Status",
                            ]}
                            data={media.map((m) => ({
                                type: m.message_type,
                                lead: `#${m.lead_id}`,
                                rep: m.rep_id,
                                duration: m.duration,
                                size: m.media_size
                                    ? `${Math.round(m.media_size / 1024)}kb`
                                    : "—",
                                status: m.status,
                            }))}
                        />
                    </Card>
                );
            case "Conversations":
                return (
                    <Card title="Conversation Audit">
                        <Table
                            columns={[
                                "Lead",
                                "Rep",
                                "Type",
                                "Direction",
                                "Time",
                            ]}
                            data={conversations.map(c => ({
                                lead: `#${c.lead_id}`,
                                rep: c.rep_id || "—",
                                type: c.message_type,
                                dir: c.direction,
                                time: new Date(c.created_at).toLocaleString(),
                            }))}
                        />
                    </Card>
                );

            case "Sales Team":
                return (
                    <Card title="Sales Team">
                        <Table
                            columns={["ID", "Name", "Status"]}
                            data={salesTeam.map((r) => ({
                                id: r.id,
                                name: r.name,
                                status: r.status,
                            }))}
                        />
                    </Card>
                );

            case "Analytics":
                return (
                    <Card title="Analytics">
                        <p className="text-sm text-gray-600">
                            Total Messages: {analytics.totalMessages}
                        </p>
                        <p className="text-sm text-gray-600">
                            Voice Messages: {analytics.voiceMessages}
                        </p>
                    </Card>
                );

            default:
                return (
                    <Card title={activeTab}>
                        <p className="text-sm text-gray-500">
                            Configuration screen
                        </p>
                    </Card>
                );
        }
    };
   
      
    /* =========================
       UI
    ========================= */
    return (
        <div className="flex h-screen bg-gray-50">
            {/* SIDEBAR */}
            <aside className="w-64 bg-white border-r p-5">
                <h1 className="text-lg font-semibold mb-6">
                    FitHer Admin
                </h1>

                <nav className="space-y-1">
                    {TABS.map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`w-full text-left px-3 py-2 rounded text-sm ${activeTab === tab
                                    ? "bg-teal-600 text-white"
                                    : "text-gray-700 hover:bg-gray-100"
                                }`}
                        >
                            {tab}
                        </button>
                    ))}
                </nav>
            </aside>

            {/* CONTENT */}
            <main className="flex-1 p-6 overflow-y-auto">
                {renderContent()}
            </main>
        </div>
    );
}
